import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { AdminPageHeader } from './AdminLayout';
import { Modal, Input, Check } from './AdminLangCountry';
import { Loader2, Save, ArrowLeft, Plus, Trash2, Edit3, ChevronDown, ChevronRight, Star } from 'lucide-react';

export function AdminArticlesList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get('/admin/articles').then((r) => setList(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (slug) => { if (!window.confirm('Delete this article?')) return; await api.delete(`/admin/articles/${slug}`); load(); };

  return (
    <div>
      <AdminPageHeader title="Articles" description="Blog articles shown on the public site at /blog. Edit title, excerpt, content and SEO per language."
        actions={<Link to="/admin/articles/new" className="btn-pill"><Plus size={16} /><span>New article</span></Link>} />
      {loading ? <Loader2 className="animate-spin" /> : (
        <div className="rounded-3xl bg-white border border-black/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500">
              <tr><th className="text-left px-5 py-3">Slug</th><th className="text-left px-5 py-3">Title (EN)</th><th className="text-left px-5 py-3">Category</th><th className="text-left px-5 py-3">Date</th><th className="text-left px-5 py-3">Status</th><th></th></tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.slug} className="border-t border-black/5 hover:bg-stone-50">
                  <td className="px-5 py-3 font-mono text-xs">{a.slug}</td>
                  <td className="px-5 py-3 font-medium flex items-center gap-2">
                    {a.featured && <Star size={12} className="text-amber-500 fill-amber-500" />}
                    {(a.translations?.en?.title) || '—'}
                  </td>
                  <td className="px-5 py-3 text-stone-600">{a.category || '—'}</td>
                  <td className="px-5 py-3 text-stone-600">{a.date}</td>
                  <td className="px-5 py-3">{a.published ? <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Published</span> : <span className="text-xs text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">Draft</span>}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Link to={`/admin/articles/${a.slug}`} className="p-2 inline-flex hover:text-[#1E2DFF]"><Edit3 size={14} /></Link>
                    <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" className="p-2 inline-flex text-xs uppercase tracking-widest text-stone-500 hover:text-[#0F0F10]">View</a>
                    <button onClick={() => remove(a.slug)} className="p-2 hover:text-red-600"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan={6} className="text-center py-12 text-stone-500">No articles yet. Click "New article" to create one.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const EMPTY_TRANSLATION = { title: '', excerpt: '', content: '', seo_title: '', seo_description: '', seo_keywords: '' };

export function AdminArticleEditor() {
  const { slug } = useParams();
  const nav = useNavigate();
  const isNew = !slug;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [languages, setLanguages] = useState([]);
  const [open, setOpen] = useState({});
  const [a, setA] = useState({
    slug: '', image: '/images/blog-1.webp', category: 'insights', date: new Date().toISOString().slice(0, 10),
    reading_minutes: 5, author_name: '', author_role: '', featured: false, published: true, translations: {},
  });

  useEffect(() => {
    api.get('/admin/languages').then((r) => {
      const langs = (r.data || []).filter((l) => l.active);
      setLanguages(langs);
      const def = langs.find((l) => l.is_default) || langs[0];
      setOpen({ [def?.code || 'en']: true });
      // Pre-fill translations slots for each active language (new article only)
      if (isNew) {
        setA((prev) => {
          const t = { ...prev.translations };
          langs.forEach((l) => { if (!t[l.code]) t[l.code] = { ...EMPTY_TRANSLATION }; });
          return { ...prev, translations: t };
        });
      }
    });
    if (!isNew) {
      api.get(`/admin/articles/${slug}`).then((r) => setA(r.data)).catch(() => nav('/admin/articles', { replace: true })).finally(() => setLoading(false));
    }
  }, [slug, isNew, nav]);

  const setField = (k, v) => setA((s) => ({ ...s, [k]: v }));
  const setT = (lang, field, v) => setA((s) => ({ ...s, translations: { ...s.translations, [lang]: { ...(s.translations[lang] || EMPTY_TRANSLATION), [field]: v } } }));

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const payload = { ...a };
      if (isNew) {
        await api.post('/admin/articles', payload);
        nav(`/admin/articles/${payload.slug}`);
      } else {
        const saved = await api.put(`/admin/articles/${slug}`, payload);
        if (saved.data?.slug && saved.data.slug !== slug) nav(`/admin/articles/${saved.data.slug}`);
        setMsg('Saved successfully.');
      }
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="animate-spin" />;

  const trFields = ['title', 'excerpt', 'content', 'seo_title', 'seo_description', 'seo_keywords'];

  return (
    <div>
      <Link to="/admin/articles" className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-[#0F0F10] mb-4"><ArrowLeft size={14} /> All articles</Link>
      <AdminPageHeader
        title={isNew ? 'New article' : (a.translations?.en?.title || a.slug)}
        description={isNew ? 'Publish a new blog article for the public site.' : `Editing /blog/${a.slug}`}
        actions={(
          <div className="flex items-center gap-3">
            {msg && <span className="text-sm text-stone-600">{msg}</span>}
            <button onClick={save} disabled={saving || !a.slug} className="btn-pill">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /><span>Save</span></>}
            </button>
          </div>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-black/10 p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Slug (URL, e.g. my-article)" value={a.slug} onChange={(e) => setField('slug', e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase())} />
            <Input label="Image URL" value={a.image || ''} onChange={(e) => setField('image', e.target.value)} />
            <Input label="Category" value={a.category || ''} onChange={(e) => setField('category', e.target.value)} />
            <Input label="Date (YYYY-MM-DD)" value={a.date || ''} onChange={(e) => setField('date', e.target.value)} />
            <Input label="Reading minutes" type="number" value={a.reading_minutes || 5} onChange={(e) => setField('reading_minutes', Number(e.target.value))} />
            <Input label="Author name" value={a.author_name || ''} onChange={(e) => setField('author_name', e.target.value)} />
            <Input label="Author role" value={a.author_role || ''} onChange={(e) => setField('author_role', e.target.value)} />
          </div>
          <div className="flex gap-5 pt-2">
            <Check label="Featured" checked={a.featured} onChange={(e) => setField('featured', e.target.checked)} />
            <Check label="Published" checked={a.published} onChange={(e) => setField('published', e.target.checked)} />
          </div>
        </div>
        <div className="rounded-3xl bg-[#F4E94B] border border-black/10 p-6">
          <p className="text-xs uppercase tracking-widest font-semibold">Tip</p>
          <p className="mt-2 text-sm text-stone-800 leading-relaxed">Write the content in plain text. Use <code className="bg-black/10 px-1 rounded">## Heading</code> for section titles, <code className="bg-black/10 px-1 rounded">**bold**</code> for emphasis and <code className="bg-black/10 px-1 rounded">- item</code> or <code className="bg-black/10 px-1 rounded">1. item</code> for lists.</p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {languages.map((l) => {
          const isOpen = !!open[l.code];
          const tr = a.translations[l.code] || EMPTY_TRANSLATION;
          return (
            <div key={l.code} className="rounded-2xl bg-white border border-black/10 overflow-hidden">
              <button onClick={() => setOpen((s) => ({ ...s, [l.code]: !isOpen }))} className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50">
                <span className="flex items-center gap-3">
                  <span className="text-xs uppercase tracking-widest font-bold bg-[#F4E94B] px-2 py-1 rounded">{l.code}</span>
                  <span className="font-semibold">{l.native_name}</span>
                  {l.is_default && <span className="text-xs bg-[#1E2DFF] text-white px-2 py-0.5 rounded">default</span>}
                </span>
                {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              {isOpen && (
                <div className="px-5 pb-6 space-y-4">
                  <label className="block">
                    <span className="text-xs text-stone-600 font-mono">title</span>
                    <input value={tr.title || ''} onChange={(e) => setT(l.code, 'title', e.target.value)} className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-stone-600 font-mono">excerpt</span>
                    <textarea rows={3} value={tr.excerpt || ''} onChange={(e) => setT(l.code, 'excerpt', e.target.value)} className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-stone-600 font-mono">content (markdown-lite)</span>
                    <textarea rows={16} value={tr.content || ''} onChange={(e) => setT(l.code, 'content', e.target.value)} className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm font-mono" />
                  </label>
                  <div className="pt-2 border-t border-black/5">
                    <p className="text-xs uppercase tracking-widest font-semibold text-stone-500 mt-4 mb-2">SEO</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="block"><span className="text-xs text-stone-600 font-mono">seo_title</span><input value={tr.seo_title || ''} onChange={(e) => setT(l.code, 'seo_title', e.target.value)} className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm" /></label>
                      <label className="block"><span className="text-xs text-stone-600 font-mono">seo_keywords</span><input value={tr.seo_keywords || ''} onChange={(e) => setT(l.code, 'seo_keywords', e.target.value)} className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm" /></label>
                      <label className="block sm:col-span-2"><span className="text-xs text-stone-600 font-mono">seo_description</span><textarea rows={2} value={tr.seo_description || ''} onChange={(e) => setT(l.code, 'seo_description', e.target.value)} className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm" /></label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
